<template>
  <div>
    <!-- Home button -->
    <div class="position-absolute m-3" style="z-index: 1000;">
      <router-link to="/" class="btn btn-secondary">Home</router-link>
    </div>
    <div class="container d-flex justify-content-center align-items-center" style="min-height: 100vh;">
      <div class="col-md-8">
        <div class="card">
          <div class="card-header bg-primary text-white text-center">
            <h3 class="mb-0">YouTube Video Uploader</h3>
            <small>For Golingo</small>
          </div>
          <div class="card-body">
            <!-- Authentication Section -->
            <div class="mb-4 d-flex flex-column align-items-center" id="yt-auth-section">
              <div v-if="!isAuthenticated">
                <button id="yt-signin-button" class="btn btn-success custom-btn me-2" @click="handleYTAuthClick">
                  Sign In with Google
                </button>
              </div>
              <div v-else>
                <p class="mb-2">You are signed in.</p>
                <button id="yt-resignin-button" class="btn btn-warning custom-btn me-2" @click="handleYTAuthClick">
                  Re-sign in with Google
                </button>
                <button id="yt-signout-button" class="btn btn-danger custom-btn" @click="handleYTSignOut">
                  Sign Out
                </button>
              </div>
            </div>
            <!-- Upload Section -->
            <div v-if="isAuthenticated" id="yt-upload-section">
              <h4 class="mb-3 text-center">Upload a Video</h4>
              <form id="yt-upload-form" @submit.prevent="uploadYTVideo">
                <div class="mb-3">
                  <label for="yt-video-title" class="form-label">Video Title:</label>
                  <input id="yt-video-title" v-model="videoTitle" type="text" class="form-control" placeholder="Enter video title">
                </div>
                <div class="mb-3">
                  <label for="yt-video-description" class="form-label">Video Description:</label>
                  <textarea id="yt-video-description" v-model="videoDescription" rows="4" class="form-control" placeholder="Enter video description"></textarea>
                </div>
                <div class="mb-3">
                  <label for="yt-video-file" class="form-label">Select Video File:</label>
                  <input id="yt-video-file" ref="ytVideoFile" type="file" accept="video/*" class="form-control">
                </div>
                <div class="d-grid gap-2">
                  <button type="button" id="yt-upload-button" class="btn btn-primary custom-btn" @click="uploadYTVideo">
                    Upload Video
                  </button>
                </div>
                <div id="yt-progress" class="mt-3 text-center">
                  <span v-if="progressMessage">
                    {{ progressMessage }}
                  </span>
                </div>
              </form>
            </div>
          </div>
          <div class="card-footer text-center text-muted">
            &copy; 2023 Golingo - YouTube Module
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "YouTubeUploader",
  data() {
    return {
      videoTitle: "",
      videoDescription: "",
      progressMessage: "",
      isAuthenticated: false,
      ytAccessToken: null,
      // For now a dummy user_id; update when you have a real user identifier.
      user_id: localStorage.getItem("userEmail") || "test_user"
    };
  },
  mounted() {
    // Check if our backend already has a token stored for the current user.
    this.checkAuthStatus();
    // Optionally check localStorage if your flow relies on that.
    const storedToken = localStorage.getItem("yt_access_token");
    const tokenExpiry = localStorage.getItem("yt_token_expiry");
    if (storedToken && tokenExpiry && new Date().getTime() < parseInt(tokenExpiry, 10)) {
      this.ytAccessToken = storedToken;
    }
  },
  methods: {
    async checkAuthStatus() {
      try {
        const endpoint = "https://806d425v42.execute-api.us-east-1.amazonaws.com/prod/youtube-check-if-got-auth-token";
        const url = endpoint + "?user_id=" + encodeURIComponent(this.user_id);
        const response = await fetch(url);
        const responseData = await response.json();
        // Expect a boolean true/false from the backend.
        this.isAuthenticated = responseData === true;
      } catch (error) {
        console.error("Error checking authentication status:", error);
        this.isAuthenticated = false;
      }
    },
    // Build the Google OAuth URL; the Lambda function uses the "state" parameter for redirecting back.
    handleYTAuthClick() {
      const clientId = "364162722608-thvnubqhtg68f1uhvf7gm5cqia8fhnbs.apps.googleusercontent.com";
      // This is your API Gateway endpoint configured to trigger the Lambda function that handles consent and token exchange.
      const apiGatewayCallbackUrl = "https://806d425v42.execute-api.us-east-1.amazonaws.com/prod/youtube-user-consent-callback";
      // After token processing, the Lambda function will redirect back to this URL.
      const vueRedirectUri = window.location.origin + window.location.pathname;
      const scope = "https://www.googleapis.com/auth/youtube.upload";
      const stateObject = {
        user_id: localStorage.getItem("userEmail") || "test_user",
        client_url: window.location.origin + window.location.pathname
      };
     
      const state = btoa(JSON.stringify(stateObject));
      const params = {
        response_type: "code",
        client_id: clientId,
        redirect_uri: apiGatewayCallbackUrl,
        scope: scope,
        access_type: "offline",  // Request a true refresh token.
        prompt: "consent",       // Force consent so a refresh token is always returned.
        state: state,   // has user_id and current url info
      };
      const queryString = Object.keys(params)
        .map(key => key + "=" + encodeURIComponent(params[key]))
        .join("&");
      const authorizationUrl = "https://accounts.google.com/o/oauth2/v2/auth?" + queryString;
      window.location.href = authorizationUrl;
    },
    // Sign out: clear any stored token and update UI accordingly.
    handleYTSignOut() {
      this.ytAccessToken = null;
      localStorage.removeItem("yt_access_token");
      localStorage.removeItem("yt_token_expiry");
      this.isAuthenticated = false;
    },
    // Upload video by generating an S3 presigned URL first and using it to PUT the video file.
    async uploadYTVideo() {
      const fileInput = this.$refs.ytVideoFile;
      const file = fileInput.files[0];
      if (!file) {
        alert("Please select a video file.");
        return;
      }
      const title = this.videoTitle || "Untitled Video";
      const description = this.videoDescription || "";
      
      // Build the payload for generating the S3 link.
      const payload = {
        customer_id: this.user_id,
        title: title,
        description: description,
        filename: file.name
      };

      try {
        // Generate the S3 presigned URL via your API.
        const endpoint = "https://806d425v42.execute-api.us-east-1.amazonaws.com/prod/youtube-s3-link";
        const response = await fetch(endpoint, {
          method: "POST",
          headers: {
            "Content-Type": "application/json"
          },
          body: JSON.stringify(payload)
        });
        const resData = await response.json();
		console.log(resData);
        if (!resData.body.url) {
          alert("Failed to acquire S3 upload URL.");
          return;
        }
        const presignedUrl = resData.body.url;
        
        // Now use XMLHttpRequest to PUT the file to S3 and track progress.
        const xhr = new XMLHttpRequest();
        xhr.open("PUT", presignedUrl, true);
        xhr.setRequestHeader("Content-Type", file.type || "video/mp4");
        
        xhr.upload.addEventListener("progress", (e) => {
          if (e.lengthComputable) {
            const progress = Math.round((e.loaded / e.total) * 100);
            this.progressMessage = "Uploading: " + progress + "%";
          }
        });
        
        xhr.onload = () => {
          if (xhr.status === 200) {
            alert("Upload complete!");
          } else {
            alert("Upload failed. Status: " + xhr.status);
          }
          this.progressMessage = "";
        };
        
        xhr.onerror = () => {
          alert("Upload failed due to a network error.");
          this.progressMessage = "";
        };
        
        xhr.send(file);
      } catch (error) {
        console.error("Error during upload:", error);
        alert("Error during upload: " + error);
      }
    }
  }
};
</script>

<style scoped>
@import "https://cdn.jsdelivr.net/npm/bootstrap@5.3.0/dist/css/bootstrap.min.css";

body {
  background: #f8f9fa;
  margin: 0;
  min-height: 100vh;
}
.card {
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}
.custom-btn {
  min-width: 150px;
}
</style>